/*
 * Do not remove or alter the notices in this preamble.
 * This software code regards iO Custom Software.
 * Copyright © 2025 iO The Netherlands B.V. and/or its affiliates.
 * www.iodigital.com All rights reserved. License grant and user rights and obligations
 * according to applicable license agreement. Please contact legal@iodigital.com for
 * questions regarding license and user rights.
 */

import { t } from 'i18next';
import { LitElement, TemplateResult, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';

import { iconChevronLink } from '@/components/icons';
import { baseStyles, rootVariablesStyles } from '@/styles';

@customElement('fg-pagination')
export class FgPagination extends LitElement {
  /**
   * The total number of pages.
   */
  @property({ type: Number })
  pages!: number;

  /**
   * The current page.
   */
  @property({ type: Number })
  currentPage = 1;

  /**
   * Used in the {@link calculatePagesInPagination} tot calculate the first page visible in the pagination.
   */
  @state()
  firstPage = 1;

  /**
   * Used in the {@link calculatePagesInPagination} tot calculate the last page visible in the pagination.
   */
  @state()
  lastPage = 5;

  /**
   * An array containing the five page numbers to be displayed in the pagination.
   */
  @state()
  pageNumbers: number[] = [];

  firstUpdated() {
    this.calculatePagesInPagination();
  }

  /**
   * Updates the current page and dispatches a custom event to notify the parent component to do got the the given page.
   *
   * @param {number} page
   */
  goToPage(page: number) {
    this.currentPage = page;

    this.calculatePagesInPagination();

    this.dispatchEvent(
      new CustomEvent('go-to-page', {
        detail: {
          page: page,
        },
        composed: true,
      }),
    );
  }

  /**
   * Calculates the pages to be displayed in the pagination. If the total pages is larger than 5, the first and last
   * page will be updated based on the current page.
   */
  calculatePagesInPagination() {
    this.pageNumbers = [];

    if (this.pages < 5) {
      this.lastPage = this.pages;
    }

    if (this.currentPage === this.lastPage && this.currentPage < this.pages) {
      this.lastPage = this.lastPage + 1;
      this.firstPage = this.firstPage + 1;
    }
    if (this.currentPage === this.firstPage && this.currentPage > 1) {
      this.lastPage = this.lastPage - 1;
      this.firstPage = this.firstPage - 1;
    }

    for (let i = this.firstPage; i <= this.lastPage; i++) {
      this.pageNumbers.push(i);
    }
  }

  /**
   * Renders a maximum of 5 page buttons, that are calculated in the {@link calculatePagesInPagination} method.
   *
   * @param page
   * @returns {TemplateResult} A button containing the page number, with a click event to go to the given page.
   */
  renderPageButton(page: number): TemplateResult {
    return html`
      <li>
        <button
          class="pagination-page ${classMap({
            active: this.currentPage === page,
          })}"
          aria-current=${ifDefined(
            this.currentPage === page ? 'page' : undefined,
          )}
          @click=${() => {
            if (this.currentPage !== page) {
              this.goToPage(page);
            }
          }}
        >
          ${page}
        </button>
      </li>
      ${page === this.lastPage
        ? nothing
        : html`
            <li class="spacer" aria-hidden="true">|</li>
          `}
    `;
  }

  render() {
    return html`
      <nav class="pagination-wrapper" aria-labelledby="pagination-title">
        <p id="pagination-title" class="sr-only">
          ${t('accessibility.pagination.paginationTitle')}
        </p>
        <ul class="pagination__list">
          ${this.currentPage === 1
            ? nothing
            : html`
                <li>
                  <button
                    class="pagination-back"
                    @click=${() => this.goToPage(this.currentPage - 1)}
                  >
                    <span class="sr-only">
                      ${t('accessibility.pagination.previous')}
                    </span>
                    <span aria-hidden="true">${iconChevronLink}</span>
                  </button>
                </li>
              `}
          ${this.pageNumbers.map(page => this.renderPageButton(page))}
          ${this.currentPage === this.pages && this.pages < 5
            ? nothing
            : html`
                <li>
                  <button
                    class="pagination-next"
                    @click=${() => this.goToPage(this.currentPage + 1)}
                  >
                    <span class="sr-only">
                      ${t('accessibility.pagination.next')}
                    </span>
                    <span aria-hidden="true">${iconChevronLink}</span>
                  </button>
                </li>
              `}
        </ul>
      </nav>
    `;
  }

  static styles = [
    baseStyles,
    rootVariablesStyles,
    css`
      :host {
        display: flex;
        justify-content: center;
        width: 100%;
      }

      .pagination__list {
        align-items: center;
        display: flex;
        justify-content: center;
        margin: 0 12px;
      }

      button {
        padding: 0 12px;
      }

      .pagination-page:hover {
        font-weight: 900;
      }

      .pagination-page.active {
        cursor: default;
        font-weight: 900;
      }

      .pagination-back,
      .pagination-next {
        aspect-ratio: 1 / 1;
        border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
        border-radius: var(
          --fg-borderradius--xs,
          var(--fg-fallback-borderradius--xs)
        );
        display: grid;
        height: 32px;
        padding: 0;
        place-items: center;
        width: 32px;
      }

      .pagination-back svg,
      .pagination-next svg {
        color: var(--fg-secondary, var(--fg-fallback-secondary));
        height: auto;
        width: 8px;
      }

      .pagination-back svg {
        transform: rotate(180deg);
      }

      .spacer {
        color: var(---fg-gray--800, var(--fg-fallback-gray--800));
        font-size: 0.75rem;
      }
    `,
  ];
}
